import { useEffect, useState } from 'react';
import { Alert, Button, Space, Table, Tag, Tooltip, message } from 'antd';
import { FileTextOutlined, PlusOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { patientApi, Patient } from '../../api/patientApi';
import dermatoApi, { DermatoForm, FormStatus } from '../../api/dermatoApi';
import hepatoApi, { HepatoForm, HepatoFormStatus } from '../../api/hepatoApi';
import { useAuthStore } from '../../store/authStore';
import { canUseDermato, canUseHepato, getProgramOptionsForRole, getRoleProgramType } from '../../utils/flowAccess';

const programLabel: Record<Patient['programType'], string> = {
  DERMATO: 'Dermato',
  HEPATO: 'Hepato',
  BOTH: 'Ambele',
};

type DashboardFilter = 'all' | 'dermato-submitted' | 'hepato-submitted' | 'drafts' | 'today';

const dashboardFilterLabels: Record<DashboardFilter, string> = {
  all: 'Toti pacientii',
  'dermato-submitted': 'Pacienti cu Dermato trimis',
  'hepato-submitted': 'Pacienti cu Hepato trimis',
  drafts: 'Pacienti cu drafturi',
  today: 'Pacienti cu chestionare create azi',
};

const isDashboardFilter = (value: string | null): value is DashboardFilter => (
  value === 'all'
  || value === 'dermato-submitted'
  || value === 'hepato-submitted'
  || value === 'drafts'
  || value === 'today'
);

const submittedDermatoStatuses = [FormStatus.SUBMITTED, FormStatus.REVIEWED];
const submittedHepatoStatuses = [HepatoFormStatus.SUBMITTED, HepatoFormStatus.REVIEWED];
const pageSize = 100;

const PatientsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const roleProgramType = getRoleProgramType(roleName);
  const allowDermato = canUseDermato(roleName);
  const allowHepato = canUseHepato(roleName);
  const dashboardFilter = searchParams.get('dashboardFilter');
  const parsedDashboardFilter = isDashboardFilter(dashboardFilter) ? dashboardFilter : null;
  const activeDashboardFilter = isAllowedDashboardFilter(parsedDashboardFilter, allowDermato, allowHepato)
    ? parsedDashboardFilter
    : null;

  const loadPatients = async () => {
    try {
      setLoading(true);
      const result = await loadAllPatients(roleProgramType);

      if (!activeDashboardFilter || activeDashboardFilter === 'all') {
        setPatients(result);
        setTotal(result.length);
        return;
      }

      const patientIds = await loadFilteredPatientIds(activeDashboardFilter, allowDermato, allowHepato);
      const filteredPatients = result.filter((patient) => patientIds.has(patient.id));

      setPatients(filteredPatients);
      setTotal(filteredPatients.length);
    } catch (error) {
      message.error('Eroare la incarcarea pacientilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, [activeDashboardFilter, roleProgramType, allowDermato, allowHepato]);

  const clearDashboardFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('dashboardFilter');
    setSearchParams(nextParams);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Pacienti</h1>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadPatients}>
            Reincarca
          </Button>
          {getProgramOptionsForRole(roleName).length > 0 && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/patients/new')}>
            Pacient Nou
          </Button>
          )}
        </Space>
      </div>

      {activeDashboardFilter && (
        <Alert
          type="info"
          showIcon
          message={dashboardFilterLabels[activeDashboardFilter]}
          action={(
            <Button size="small" onClick={clearDashboardFilter}>
              Sterge filtrul
            </Button>
          )}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={loading}
        dataSource={patients}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: () => `Total ${total} pacienti`,
        }}
        columns={[
          {
            title: 'Nume',
            key: 'name',
            render: (_: unknown, patient: Patient) => `${patient.lastName} ${patient.firstName}`,
            sorter: (a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
          },
          {
            title: 'CNP',
            dataIndex: 'cnp',
            key: 'cnp',
          },
          {
            title: 'Program',
            dataIndex: 'programType',
            key: 'programType',
            render: (programType: Patient['programType']) => <Tag color="blue">{programLabel[programType]}</Tag>,
          },
          {
            title: 'Telefon',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone: string | null) => phone || '-',
          },
          {
            title: 'Creat la',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
            sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: Patient['status']) => (
              <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>
                {status === 'ACTIVE' ? 'Activ' : status}
              </Tag>
            ),
          },
          {
            title: 'Actiuni',
            key: 'actions',
            render: (_: unknown, patient: Patient) => {
              // Registrul importat: spațiile de lucru pe faze (au stare goală grațioasă
              // când pacientul nu are încă fișă importată); nu depind de feature flag-uri.
              const showHepato = patient.programType === 'HEPATO' || patient.programType === 'BOTH';
              const showDermato = patient.programType === 'DERMATO' || patient.programType === 'BOTH';
              return (
                <Space size="small">
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/patients/${patient.id}`)}
                  />
                  {showHepato && (
                    <Tooltip title={!allowHepato ? 'Rolul tău nu are acces la fluxul Hepato' : undefined}>
                      <Button
                        type="primary"
                        icon={<FileTextOutlined />}
                        disabled={!allowHepato}
                        onClick={() => navigate(`/patients/${patient.id}/hepato`)}
                      >
                        Fișa hepato
                      </Button>
                    </Tooltip>
                  )}
                  {showDermato && (
                    <Tooltip title={!allowDermato ? 'Rolul tău nu are acces la fluxul Dermato' : undefined}>
                      <Button
                        type="primary"
                        icon={<FileTextOutlined />}
                        disabled={!allowDermato}
                        onClick={() => navigate(`/patients/${patient.id}/dermato`)}
                      >
                        Fișa dermato
                      </Button>
                    </Tooltip>
                  )}
                </Space>
              );
            },
          },
        ]}
      />
    </div>
  );
};

function isAllowedDashboardFilter(
  filter: DashboardFilter | null,
  allowDermato: boolean,
  allowHepato: boolean,
): filter is DashboardFilter {
  if (!filter) {
    return false;
  }

  if (filter === 'dermato-submitted') {
    return allowDermato;
  }

  if (filter === 'hepato-submitted') {
    return allowHepato;
  }

  return true;
}

async function loadFilteredPatientIds(
  filter: DashboardFilter,
  allowDermato: boolean,
  allowHepato: boolean,
): Promise<Set<string>> {
  const todayStart = dayjs().startOf('day').toISOString();
  const todayEnd = dayjs().endOf('day').toISOString();

  const toPatientIds = (forms: Array<DermatoForm | HepatoForm>) => (
    forms.reduce<Set<string>>((ids, form) => {
      ids.add(form.patientId);
      return ids;
    }, new Set<string>())
  );

  if (filter === 'dermato-submitted') {
    const results = await Promise.all(submittedDermatoStatuses.map((status) => (
      loadAllDermatoForms({ status })
    )));
    return toPatientIds(results.flat());
  }

  if (filter === 'hepato-submitted') {
    const results = await Promise.all(submittedHepatoStatuses.map((status) => (
      loadAllHepatoForms({ status })
    )));
    return toPatientIds(results.flat());
  }

  if (filter === 'drafts') {
    const [dermatoDrafts, hepatoDrafts] = await Promise.all([
      allowDermato ? loadAllDermatoForms({ status: FormStatus.DRAFT }) : Promise.resolve([]),
      allowHepato ? loadAllHepatoForms({ status: HepatoFormStatus.DRAFT }) : Promise.resolve([]),
    ]);
    return toPatientIds([...dermatoDrafts, ...hepatoDrafts]);
  }

  if (filter === 'today') {
    const [dermatoToday, hepatoToday] = await Promise.all([
      allowDermato ? loadAllDermatoForms({ createdAfter: todayStart, createdBefore: todayEnd }) : Promise.resolve([]),
      allowHepato ? loadAllHepatoForms({ createdAfter: todayStart, createdBefore: todayEnd }) : Promise.resolve([]),
    ]);
    return toPatientIds([...dermatoToday, ...hepatoToday]);
  }

  return new Set<string>();
}

async function loadAllPatients(programType?: Patient['programType']): Promise<Patient[]> {
  const firstPage = await patientApi.list({ page: 1, limit: pageSize, programType });
  const patients = [...firstPage.patients];
  const totalPages = Math.ceil(firstPage.total / pageSize);

  for (let page = 2; page <= totalPages; page += 1) {
    const result = await patientApi.list({ page, limit: pageSize, programType });
    patients.push(...result.patients);
  }

  return patients;
}

async function loadAllDermatoForms(params: {
  status?: FormStatus;
  createdAfter?: string;
  createdBefore?: string;
}): Promise<DermatoForm[]> {
  const firstPage = await dermatoApi.list({ ...params, page: 1, limit: pageSize });
  const forms = [...firstPage.data];
  const totalPages = Math.ceil(firstPage.total / pageSize);

  for (let page = 2; page <= totalPages; page += 1) {
    const result = await dermatoApi.list({ ...params, page, limit: pageSize });
    forms.push(...result.data);
  }

  return forms;
}

async function loadAllHepatoForms(params: {
  status?: HepatoFormStatus;
  createdAfter?: string;
  createdBefore?: string;
}): Promise<HepatoForm[]> {
  const firstPage = await hepatoApi.list({ ...params, page: 1, limit: pageSize });
  const forms = [...firstPage.data];
  const totalPages = Math.ceil(firstPage.total / pageSize);

  for (let page = 2; page <= totalPages; page += 1) {
    const result = await hepatoApi.list({ ...params, page, limit: pageSize });
    forms.push(...result.data);
  }

  return forms;
}

export default PatientsListPage;
